import React from "react";
import { withRouter } from "react-router-dom";
import { CircularProgress } from "material-ui";
import * as SearchService from "../../services/search-service";
import * as PaperStates from "../../general/paper-state";
import { highlight } from "../../general/search-highlight";
import { DS } from "../../theme-default";
import PaperSearchDetails from "./PaperSearchDetails";

const TYPEAHEAD_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;
const TYPEAHEAD_GROUP_SIZE = 8;

const stateColor = (state) => {
  if (PaperStates.isApproved(state)) return DS.success;
  if (PaperStates.isRejected(state)) return DS.error;
  if (PaperStates.isDraft(state)) return DS.textMuted;
  return DS.warning;
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  return (
    date.getDate() + "/" + (date.getMonth() + 1) + "/" + date.getFullYear()
  );
};

const formatValue = (value) => {
  if (value === null || value === undefined) return "";
  const number = Number(value);
  if (isNaN(number)) return "";
  return (
    number.toLocaleString("ro-RO", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + " lei"
  );
};

export class GlobalSearch extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      query: "",
      open: false,
      loading: false,
      results: null,
      error: false,
      selectedIndex: -1,
    };
    this.requestSeq = 0;
    this.containerRef = React.createRef();
    this.inputRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener("mousedown", this._onDocumentMouseDown);
    document.addEventListener("keydown", this._onDocumentKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this._onDocumentMouseDown);
    document.removeEventListener("keydown", this._onDocumentKeyDown);
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  _onDocumentMouseDown = (e) => {
    if (
      this.containerRef.current &&
      !this.containerRef.current.contains(e.target)
    ) {
      this._close();
    }
  };

  _onDocumentKeyDown = (e) => {
    const isShortcut =
      ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") ||
      (e.key === "/" &&
        !["INPUT", "TEXTAREA"].includes((e.target.tagName || "").toUpperCase()) &&
        !e.target.isContentEditable);
    if (isShortcut) {
      e.preventDefault();
      if (this.inputRef.current) {
        this.inputRef.current.focus();
        this.inputRef.current.select();
      }
    }
  };

  _onInputChange = (e) => {
    const query = e.target.value;
    this.setState({ query, selectedIndex: -1 });
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    if (query.trim().length < MIN_QUERY_LENGTH) {
      this.requestSeq++; // invalidate any in-flight request
      this.setState({ open: true, results: null, loading: false, error: false });
      return;
    }
    this.debounceTimer = setTimeout(() => {
      this._runSearch(query);
    }, TYPEAHEAD_DEBOUNCE_MS);
  };

  _runSearch = (query) => {
    const seq = ++this.requestSeq;
    this.setState({ loading: true, open: true, error: false });
    SearchService.globalSearch(query, TYPEAHEAD_GROUP_SIZE)
      .then((response) => {
        if (seq !== this.requestSeq) {
          return; // stale response, a newer query is in flight
        }
        this.setState({
          results: response.data,
          loading: false,
          open: true,
        });
      })
      .catch(() => {
        if (seq !== this.requestSeq) {
          return;
        }
        this.setState({ loading: false, results: null, error: true });
      });
  };

  _navigableItems = () => {
    const { results, loading } = this.state;
    if (!results || loading) {
      // while loading, the dropdown shows only the spinner — the previous
      // query's items are invisible, so they must not be keyboard-reachable
      return [];
    }
    const items = [];
    const papers = (results.papers && results.papers.items) || [];
    papers.forEach((paper) => items.push({ type: "paper", data: paper }));
    const contracts = (results.contracts && results.contracts.items) || [];
    contracts.forEach((contract) =>
      items.push({ type: "contract", data: contract }),
    );
    if (this._totalCount() > 0) {
      items.push({ type: "all" });
    }
    return items;
  };

  _totalCount = () => {
    const { results } = this.state;
    if (!results) {
      return 0;
    }
    const papersTotal = results.papers ? results.papers.total : 0;
    const contractsTotal = results.contracts ? results.contracts.total : 0;
    return papersTotal + contractsTotal;
  };

  _onInputKeyDown = (e) => {
    const items = this._navigableItems();
    if (e.key === "ArrowDown") {
      e.preventDefault();
      this.setState({
        open: true,
        selectedIndex: Math.min(this.state.selectedIndex + 1, items.length - 1),
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      this.setState({
        selectedIndex: Math.max(this.state.selectedIndex - 1, -1),
      });
    } else if (e.key === "Enter") {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      const selected = items[this.state.selectedIndex];
      if (selected) {
        this._openItem(selected);
      } else if (this.state.query.trim().length >= MIN_QUERY_LENGTH) {
        this._goToResultsPage();
      }
    } else if (e.key === "Escape") {
      this.setState({ open: false, selectedIndex: -1 });
      if (this.inputRef.current) {
        this.inputRef.current.blur();
      }
    }
  };

  _onFocus = () => {
    this.setState({ open: true });
  };

  _close = () => {
    this.setState({ open: false, selectedIndex: -1 });
    if (this.inputRef.current) {
      this.inputRef.current.blur();
    }
  };

  _openItem = (item) => {
    if (item.type === "paper") {
      this._close();
      this.props.history.push(
        "/app/referate/0?filter=all&q=" + encodeURIComponent(item.data.code),
      );
    } else if (item.type === "contract") {
      const year = item.data.contractDate
        ? new Date(item.data.contractDate).getFullYear()
        : new Date().getFullYear();
      this._close();
      this.props.history.push(
        "/app/contracts?year=" +
          year +
          "&q=" +
          encodeURIComponent(item.data.contractNr || ""),
      );
    } else if (item.type === "all") {
      this._goToResultsPage();
    }
  };

  _goToResultsPage = () => {
    this._close();
    this.props.history.push(
      "/app/search?q=" + encodeURIComponent(this.state.query.trim()),
    );
  };

  _renderGroupHeader = (label, total) => (
    <div
      style={{
        padding: "8px 14px 4px 14px",
        fontSize: "12px",
        fontWeight: 600,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        color: DS.textMuted,
      }}
    >
      {label}
      {total > 0 && (
        <span style={{ float: "right", fontWeight: 400 }}>{total}</span>
      )}
    </div>
  );

  _itemStyle = (isSelected) => ({
    cursor: "pointer",
    backgroundColor: isSelected ? DS.bg : DS.surface,
  });

  _renderPaperItem = (paper, index) => {
    const isSelected = index === this.state.selectedIndex;
    const query = this.state.query;
    return (
      <div
        key={"paper-" + paper.id}
        className={
          "ds-global-search-result" +
          (isSelected ? " ds-global-search-result--selected" : "")
        }
        style={this._itemStyle(isSelected)}
        role="option"
        aria-selected={isSelected}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => this._openItem({ type: "paper", data: paper })}
        onMouseEnter={() => this.setState({ selectedIndex: index })}
      >
        <div className="ds-global-search-result-header">
          <span className="ds-global-search-result-code">
            {highlight(paper.code, query)}
          </span>
          <span style={{ color: stateColor(paper.state), fontWeight: 600 }}>
            {PaperStates.getTypeForId(paper.state)}
          </span>
          {paper.createdDate && <span>{formatDate(paper.createdDate)}</span>}
          {paper.totalPriceWithVat !== null &&
            paper.totalPriceWithVat !== undefined && (
              <span>{formatValue(paper.totalPriceWithVat)}</span>
            )}
        </div>

        <PaperSearchDetails paper={paper} query={query} />

        <div className="ds-global-search-result-meta">
          {paper.department && (
            <span>
              <strong>Compartiment:</strong> {highlight(paper.department, query)}
            </span>
          )}
          {paper.acquisitionCategory && (
            <span>
              <strong>Categorie:</strong>{" "}
              {highlight(paper.acquisitionCategory, query)}
            </span>
          )}
          {paper.financeSource && (
            <span>
              <strong>Sursa:</strong> {highlight(paper.financeSource, query)}
            </span>
          )}
          {paper.createdBy && (
            <span>
              <strong>Creat de:</strong> {highlight(paper.createdBy, query)}
            </span>
          )}
        </div>
      </div>
    );
  };

  _renderContractItem = (contract, index) => {
    const isSelected = index === this.state.selectedIndex;
    const query = this.state.query;
    return (
      <div
        key={"contract-" + contract.id}
        className={
          "ds-global-search-result" +
          (isSelected ? " ds-global-search-result--selected" : "")
        }
        style={this._itemStyle(isSelected)}
        role="option"
        aria-selected={isSelected}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => this._openItem({ type: "contract", data: contract })}
        onMouseEnter={() => this.setState({ selectedIndex: index })}
      >
        <div
          style={{
            fontSize: "15px",
            color: DS.ink,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ fontWeight: 600, marginRight: 8 }}>
            {highlight("Nr. " + (contract.contractNr || "-"), query)}
          </span>
          {highlight(contract.contractObject || contract.serviceName, query)}
        </div>
        <div
          style={{
            fontSize: "13px",
            color: DS.textSecondary,
            marginTop: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {contract.supplier && (
            <span>{highlight(contract.supplier, query)}</span>
          )}
          {contract.contractDate && (
            <span> · {formatDate(contract.contractDate)}</span>
          )}
        </div>
      </div>
    );
  };

  _renderDropdown = () => {
    const { results, loading } = this.state;
    const items = this._navigableItems();
    const papers = (results && results.papers && results.papers.items) || [];
    const contracts =
      (results && results.contracts && results.contracts.items) || [];
    const contractsVisible =
      results && results.contracts !== null && results.contracts !== undefined;
    const totalCount = this._totalCount();

    let cursor = 0;

    return (
      <div
        id="global-search-results"
        className="ds-global-search-dropdown"
        role="listbox"
      >
        {!loading && this.state.query.trim().length < MIN_QUERY_LENGTH && (
          <div className="ds-global-search-help">
            <strong>Caută în referate</strong>
            <span>
              După justificare, articole, cod, compartiment, categorie, sursă
              de finanțare sau autor.
            </span>
          </div>
        )}

        {loading && (
          <div style={{ textAlign: "center", padding: 16 }}>
            <CircularProgress size={24} thickness={2.5} />
          </div>
        )}

        {!loading && this.state.error && (
          <div
            style={{
              padding: "18px 14px",
              color: DS.error,
              fontSize: "15px",
              textAlign: "center",
            }}
          >
            Cautarea a esuat. Va rog reincercati.
          </div>
        )}

        {!loading && results && totalCount === 0 && (
          <div
            style={{
              padding: "18px 14px",
              color: DS.textSecondary,
              fontSize: "15px",
              textAlign: "center",
            }}
          >
            Niciun rezultat pentru „{this.state.query.trim()}”
          </div>
        )}

        {!loading && papers.length > 0 && (
          <div>
            {this._renderGroupHeader(
              "Referate",
              results.papers ? results.papers.total : 0,
            )}
            {papers.map((paper) => this._renderPaperItem(paper, cursor++))}
          </div>
        )}

        {!loading && contractsVisible && contracts.length > 0 && (
          <div>
            {this._renderGroupHeader(
              "Contracte",
              results.contracts ? results.contracts.total : 0,
            )}
            {contracts.map((contract) =>
              this._renderContractItem(contract, cursor++),
            )}
          </div>
        )}

        {!loading && totalCount > 0 && (
          <div
            className="ds-global-search-all-results"
            style={{
              ...this._itemStyle(cursor === this.state.selectedIndex),
              color: DS.primary,
            }}
            role="option"
            aria-selected={cursor === this.state.selectedIndex}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => this._openItem({ type: "all" })}
            onMouseEnter={() => this.setState({ selectedIndex: items.length - 1 })}
          >
            Vezi toate rezultatele ({totalCount})
          </div>
        )}
      </div>
    );
  };

  render() {
    const { open } = this.state;
    return (
      <div
        className={
          "ds-global-search" + (open ? " ds-global-search--open" : "")
        }
      >
        {open && <div className="ds-global-search-backdrop" />}
        <div ref={this.containerRef} className="ds-global-search-panel">
          <div className="ds-global-search-input-row">
            {open && <span className="material-icons">search</span>}
            <input
              ref={this.inputRef}
              className="ds-global-search-input"
              type="search"
              name="app-global-search"
              autoComplete="off"
              value={this.state.query}
              onChange={this._onInputChange}
              onKeyDown={this._onInputKeyDown}
              onFocus={this._onFocus}
              placeholder="Caută după justificare, articol, cod...  (Ctrl+K)"
              aria-label="Căutare documente"
              aria-expanded={open}
              aria-controls="global-search-results"
            />
            {open && (
              <button
                type="button"
                className="ds-global-search-close"
                onClick={this._close}
                aria-label="Închide căutarea"
              >
                ×
              </button>
            )}
          </div>
          {open && this._renderDropdown()}
        </div>
      </div>
    );
  }
}

// The contracts group is omitted server-side for users without the
// canSeeContracts permission, so no client-side gating is needed here.
export default withRouter(GlobalSearch);
